import { ENTRY_COLUMNS, HORIZON_COLUMNS, ITEM_COLUMNS, decodeText, encodeText, textBytes, toEntry, toHorizonItem, toHorizonRow, } from "./rows.js";
export class SqlEntryRepo {
    db;
    cipher;
    /**
     * `cipher` необовʼязковий: без нього тіла лягають у базу як є. Це стан
     * для тестів і для платформ, де шифрує сама база, а не поле — і він має
     * бути видимим у сигнатурі, а не вгадуватися з поведінки.
     */
    constructor(db, cipher) {
        this.db = db;
        this.cipher = cipher;
    }
    async byId(id) {
        const rows = await this.db.query(`SELECT ${ENTRY_COLUMNS} FROM entry WHERE id = ? AND deleted_at IS NULL`, [id]);
        return rows[0] ? await this.#open(toEntry(rows[0])) : null;
    }
    async #open(entry) {
        if (!this.cipher || entry.body === null)
            return entry;
        // Ідентифікатор запису — не додаткова інформація для шифру, а частина
        // ключа: у кожного запису він свій ([ADR-0016]).
        return { ...entry, body: await this.cipher.open(entry.body, entry.id) };
    }
    async byDate(date) {
        // Порядок за створенням, а не за зміною: сторінка дня не має
        // перетасовуватися під користувачем, коли він редагує ранній запис.
        const rows = await this.db.query(`SELECT ${ENTRY_COLUMNS} FROM entry
        WHERE entry_date = ? AND deleted_at IS NULL
        ORDER BY created_at, id`, [date]);
        return Promise.all(rows.map((r) => this.#open(toEntry(r))));
    }
    async inRange(fromKey, toKey) {
        // Той самий індекс `entry_by_date`, що й у `byDate`: ключі дат
        // порівнюються рядками, бо формат `YYYY-MM-DD` упорядкований лексично.
        const rows = await this.db.query(`SELECT ${ENTRY_COLUMNS} FROM entry
        WHERE entry_date >= ? AND entry_date <= ? AND deleted_at IS NULL
        ORDER BY entry_date, created_at, id`, [fromKey, toKey]);
        return Promise.all(rows.map((r) => this.#open(toEntry(r))));
    }
    async page(after, limit) {
        const rows = await this.db.query(`SELECT ${ENTRY_COLUMNS} FROM entry
        WHERE deleted_at IS NULL AND id > ?
        ORDER BY id
        LIMIT ?`, [after ?? "", limit]);
        return Promise.all(rows.map((r) => this.#open(toEntry(r))));
    }
    async byKind(kind, limit) {
        const rows = await this.db.query(`SELECT ${ENTRY_COLUMNS} FROM entry
        WHERE kind = ? AND deleted_at IS NULL
        ORDER BY created_at DESC, id DESC
        LIMIT ?`, [kind, limit]);
        return Promise.all(rows.map((r) => this.#open(toEntry(r))));
    }
    async feed(before, limit) {
        // Порядок за датою запису, а не за створенням: стрічка — це вісь дат,
        // і запис, дописаний сьогодні за минулий вівторок, належить вівторку.
        const rows = await this.db.query(`SELECT ${ENTRY_COLUMNS} FROM entry
        WHERE kind = 'journal' AND entry_date IS NOT NULL AND deleted_at IS NULL
          AND entry_date < ?
        ORDER BY entry_date DESC, created_at DESC
        LIMIT ?`, [before ?? "9999-99-99", limit]);
        return Promise.all(rows.map((r) => this.#open(toEntry(r))));
    }
    async save(entry) {
        const body = this.cipher && entry.body !== null
            ? await this.cipher.seal(entry.body, entry.id)
            : entry.body;
        const params = [
            entry.id,
            entry.kind,
            entry.title,
            body,
            entry.entryDate,
            entry.mood,
            entry.createdAt,
            entry.updatedAt,
            entry.deletedAt,
            entry.hlc,
            entry.deviceId,
        ];
        await this.db.exec(`INSERT INTO entry (${ENTRY_COLUMNS}) VALUES (?,?,?,?,?,?,?,?,?,?,?)
       ON CONFLICT(id) DO UPDATE SET
         kind = excluded.kind,
         title = excluded.title,
         body = excluded.body,
         entry_date = excluded.entry_date,
         mood = excluded.mood,
         updated_at = excluded.updated_at,
         deleted_at = excluded.deleted_at,
         hlc = excluded.hlc,
         device_id = excluded.device_id`, params);
    }
}
/** Скільки кроків ланцюга перенесень рахувати, доки не визнати дані циклом. */
const MAX_ROLL_DEPTH = 1000;
export class SqlHorizonRepo {
    db;
    ids;
    clock;
    cipher;
    horizonId;
    constructor(db, ids, clock, cipher, 
    /**
     * Ідентифікатор горизонта за його ключем — [ADR-0023].
     *
     * Приходить ззовні тією самою формою, що `ExtractText` у `SqlSearch`:
     * вивід залежить від ключа даних, а `db` про ключі не знає й не має.
     *
     * Без нього береться випадковий `ids.next()`. Це не запасний варіант
     * «про всяк випадок», а справжній стан до появи акаунта: горизонти,
     * створені тоді, отримують випадкові ідентифікатори, і саме їх потім
     * переписує `remapHorizonIds`.
     */
    horizonId) {
        this.db = db;
        this.ids = ids;
        this.clock = clock;
        this.cipher = cipher;
        this.horizonId = horizonId;
    }
    /** Текст рядка плану лежить у BLOB саме заради цього кроку. */
    async #sealText(text, itemId) {
        const bytes = encodeText(text);
        return this.cipher ? await this.cipher.seal(bytes, itemId) : bytes;
    }
    async #item(row) {
        const stored = textBytes(row, "text");
        const plain = this.cipher ? await this.cipher.open(stored, String(row["id"])) : stored;
        return toHorizonItem(row, decodeText(plain));
    }
    async find(kind, key) {
        const rows = await this.db.query(`SELECT ${HORIZON_COLUMNS} FROM horizon
        WHERE kind = ? AND key = ? AND deleted_at IS NULL`, [kind, key]);
        return rows[0] ? toHorizonRow(rows[0]) : null;
    }
    async byId(id) {
        const rows = await this.db.query(`SELECT ${HORIZON_COLUMNS} FROM horizon WHERE id = ? AND deleted_at IS NULL`, [id]);
        return rows[0] ? toHorizonRow(rows[0]) : null;
    }
    /**
     * Горизонт створюється ліниво. Гонка тут реальна: два записи в один день
     * з різних вкладок дали б два рядки на той самий ключ. Захищає унікальний
     * індекс `horizon_key` разом із `DO NOTHING` — після вставки читаємо те,
     * що справді лежить у базі, а не те, що ми щойно намагалися вставити.
     */
    async ensure(kind, key) {
        const now = this.clock.now();
        const id = this.horizonId ? this.horizonId(kind, key) : this.ids.next();
        await this.db.exec(`INSERT INTO horizon (${HORIZON_COLUMNS}) VALUES (?,?,?,?,?,?,?,?,?)
       ON CONFLICT(kind, key) DO NOTHING`, [id, kind, key, null, now, now, null, this.ids.nextHlc(), this.ids.deviceId()]);
        const found = await this.find(kind, key);
        if (found)
            return found;
        /**
         * Сюди можна потрапити єдиним шляхом: рядок є, але позначений
         * видаленим. Раніше тут був виняток — і це була міна.
         *
         * Індекс `UNIQUE(kind, key)` **не фільтрований за `deleted_at`**, тож
         * tombstone займає ключ назавжди: вставка мовчки нічого не робить,
         * а `find` його не бачить. День, на якому колись зʼявився tombstone,
         * ставав неплановним — і з синхронізацією став би таким **на всіх
         * пристроях одразу**.
         *
         * Тому воскрешаємо. Горизонт не містить нічого, що ввів користувач:
         * це адреса, а не запис. Повернути адресу до життя не означає
         * повернути видалене.
         */
        await this.db.exec(`UPDATE horizon SET deleted_at = NULL, updated_at = ?, hlc = ?, device_id = ?
        WHERE kind = ? AND key = ?`, [now, this.ids.nextHlc(), this.ids.deviceId(), kind, key]);
        const revived = await this.find(kind, key);
        if (!revived)
            throw new Error(`горизонт ${kind}:${key} не створюється й не воскресає`);
        return revived;
    }
    async itemsOf(horizonId) {
        const rows = await this.db.query(`SELECT ${ITEM_COLUMNS} FROM horizon_item
        WHERE horizon_id = ? AND deleted_at IS NULL
        ORDER BY ordinal, id`, [horizonId]);
        return Promise.all(rows.map((r) => this.#item(r)));
    }
    /**
     * Одним запитом замість запиту на горизонт.
     *
     * Ключі горизонтів одного масштабу мають сталу ширину й доповнені нулями
     * (`2026-03-07`, `2026-W09`), тому порівняння рядків збігається з
     * порівнянням дат — і `BETWEEN` працює без розбору ключа в SQL.
     */
    async itemsInRange(kind, fromKey, toKey) {
        const rows = await this.db.query(`SELECT h.key AS horizon_key, ${ITEM_COLUMNS.split(", ").map((c) => `i.${c}`).join(", ")}
         FROM horizon_item i
         JOIN horizon h ON h.id = i.horizon_id
        WHERE h.kind = ? AND h.key BETWEEN ? AND ?
          AND i.deleted_at IS NULL AND h.deleted_at IS NULL
        ORDER BY h.key, i.ordinal, i.id`, [kind, fromKey, toKey]);
        return Promise.all(rows.map(async (row) => ({
            horizonKey: String(row.horizon_key),
            item: await this.#item(row),
        })));
    }
    /**
     * Лічильники замість рядків — той самий проміжок, та сама пара умов.
     *
     * `COUNT(i.done_at)` рахує лише непорожні: у SQL агрегат пропускає NULL,
     * і це рівно визначення «зроблено» з `HorizonItem`. Окремий `CASE` тут
     * сказав би те саме довше.
     *
     * Порожній горизонт рядка не дає: `GROUP BY` рахує те, що є. Хто питає —
     * домальовує нулі сам, інакше запит мусив би знати про календар.
     */
    async countsInRange(kind, fromKey, toKey) {
        const rows = await this.db.query(`SELECT h.key AS horizon_key, COUNT(*) AS total, COUNT(i.done_at) AS done
         FROM horizon_item i
         JOIN horizon h ON h.id = i.horizon_id
        WHERE h.kind = ? AND h.key BETWEEN ? AND ?
          AND i.deleted_at IS NULL AND h.deleted_at IS NULL
        GROUP BY h.key
        ORDER BY h.key`, [kind, fromKey, toKey]);
        return rows.map((row) => ({
            horizonKey: String(row["horizon_key"]),
            done: Number(row["done"]),
            total: Number(row["total"]),
        }));
    }
    async itemById(id) {
        const rows = await this.db.query(`SELECT ${ITEM_COLUMNS} FROM horizon_item WHERE id = ? AND deleted_at IS NULL`, [id]);
        return rows[0] ? await this.#item(rows[0]) : null;
    }
    async rolledFrom(id) {
        // Без фільтра за deleted_at — на відміну від `itemById`. Саме тут
        // tombstone потрібен: ланцюг перенесень веде крізь закриті рядки.
        const rows = await this.db.query(`SELECT ${ITEM_COLUMNS} FROM horizon_item WHERE id = ?`, [
            id,
        ]);
        return rows[0] ? await this.#item(rows[0]) : null;
    }
    /**
     * Рекурсивний CTE замість проходу ланцюгом у коді.
     *
     * Обмеження глибини не косметичне: цикл у ланцюгу означав би пошкоджені
     * дані, а рекурсивний запит без межі в такому разі не завершиться взагалі.
     * Краще віддати завелике число, ніж повісити вкладку.
     */
    async rollCounts(ids) {
        const out = new Map();
        if (ids.length === 0)
            return out;
        const holes = ids.map(() => "?").join(",");
        const rows = await this.db.query(`WITH RECURSIVE chain(root, cur, n) AS (
         SELECT id, rolled_from_id, 0 FROM horizon_item WHERE id IN (${holes})
         UNION ALL
         SELECT c.root, i.rolled_from_id, c.n + 1
           FROM chain c JOIN horizon_item i ON i.id = c.cur
          WHERE c.n < ${MAX_ROLL_DEPTH}
       )
       SELECT root, MAX(n) AS n FROM chain GROUP BY root`, [...ids]);
        for (const row of rows)
            out.set(row.root, row.n);
        for (const id of ids)
            if (!out.has(id))
                out.set(id, 0);
        return out;
    }
    async saveItem(item) {
        const params = [
            item.id,
            item.horizonId,
            await this.#sealText(item.text, item.id),
            item.doneAt,
            item.ordinal,
            item.rolledFromId,
            item.spanEndKey,
            item.startTime ?? null,
            item.endTime ?? null,
            item.calendarEventId ?? null,
            item.createdAt,
            item.updatedAt,
            item.deletedAt,
            item.hlc,
            item.deviceId,
        ];
        await this.db.exec(`INSERT INTO horizon_item (${ITEM_COLUMNS}) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)
       ON CONFLICT(id) DO UPDATE SET
         text = excluded.text,
         done_at = excluded.done_at,
         ordinal = excluded.ordinal,
         rolled_from_id = excluded.rolled_from_id,
         span_end_key = excluded.span_end_key,
         start_time = excluded.start_time,
         end_time = excluded.end_time,
         calendar_event_id = excluded.calendar_event_id,
         updated_at = excluded.updated_at,
         deleted_at = excluded.deleted_at,
         hlc = excluded.hlc,
         device_id = excluded.device_id`, params);
    }
}
//# sourceMappingURL=repos.js.map